import CameraIcon from "assets/images/CameraIcon";
import {useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {common as stringsCommon, registerNewUserPage as strings} from "strings";

import AddInsuranceCardModal from "components/AddInsuranceCardModal";
import Button from "components/Button";
import {DateString} from "components/DateInput";
import PasswordRequirements from "components/PasswordRequirements/PasswordRequirements";
import Select from "components/Select";
import StateInput from "components/StateInput";
import StyledDateInput from "components/StyledDateInput";
import Switch from "components/Switch";
import TextInput from "components/TextInput";
import UnauthLayout from "components/UnauthLayout";

import {useRegisterNewUser} from "liberty-core/api/react-query/user";
import {
  isValidCity,
  isValidEmail,
  isValidFirstOrLastName,
  isValidStreet,
  isValidZipCode,
} from "liberty-core/sharedFunctions";

import {useAlert} from "lib/alert";
import {useCurrentPharmacy} from "lib/useCurrentPharmacy";

import styles from "./RegisterNewUserPage.module.css";

const errorFieldNames = {
  password: "Password",
  confirmPassword: "ConfirmPassword",
  firstName: "FirstName",
  lastName: "LastName",
  dateOfBirth: "DateOfBirth",
  email: "Email",
};

export default function RegisterNewUserPage() {
  const [searchParams] = useSearchParams();
  const [pharmacy] = useCurrentPharmacy();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDOB] = useState<DateString>("");
  const [gender, setGender] = useState(0);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [fieldErrors, setFieldErrors] = useState<{[field: string]: string[]}>(
    {},
  );

  const [noInsurance, setNoInsurance] = useState(false);
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [cardFront, setCardFront] = useState<File | null>(null);
  const [cardBack, setCardBack] = useState<File | null>(null);

  const setPasswordVal = (newPassword: string) => {
    setPassword(newPassword);
    setFieldErrors((prev) => ({
      ...prev,
      [errorFieldNames.password]: [],
      [errorFieldNames.confirmPassword]: [],
    }));
  };

  const setConfirmPasswordVal = (newConfirmPassword: string) => {
    setConfirmPassword(newConfirmPassword);
    setFieldErrors((prev) => ({
      ...prev,
      [errorFieldNames.password]: [],
      [errorFieldNames.confirmPassword]: [],
    }));
  };

  const {mutateAsync: submitRegisterRequest} = useRegisterNewUser();
  const [isLoading, setIsLoading] = useState(false);

  const alert = useAlert();

  const registerNewUser = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();

    if (isLoading) {
      return;
    }

    if (password !== confirmPassword) {
      alert({
        title: stringsCommon.whoops,
        message: strings.passwordMatchError,
      });
      setFieldErrors({
        [errorFieldNames.password]: [strings.mustMatch],
        [errorFieldNames.confirmPassword]: [strings.mustMatch],
      });
    } else {
      setIsLoading(true);
      submitRegisterRequest({
        firstName,
        lastName,
        dateOfBirth: dob,
        gender: gender !== 0 ? gender.toString() : "",
        email,
        password,
        pharmacyId: pharmacy?.id ?? 0,
        mobilePhone: phone,
        streetAddress: street,
        city,
        state,
        zip,
        guestInsuranceCardFrontImage: cardFront,
        guestInsuranceCardBackImage: cardBack,
      })
        .then((data) => {
          navigate(`/verify?${searchParams.toString()}`, {
            state: {
              selections: data.selections,
              temporaryPatientId: data.temporaryPatientId,
              isNewUser: true,
            },
          });
        })
        .catch((err) => {
          if (err?.response?.status === 400 && err?.response?.data?.errors) {
            setFieldErrors(err.response.data.errors);
          }
          if (
            err?.response?.data?.generic &&
            err.response.data.generic[0] === "User_Account_Duplicate"
          ) {
            navigate(`/?${searchParams.toString()}`);
          } else {
            alert({
              title: stringsCommon.whoops,
              message:
                err?.response?.data?.generic?.join("\n") ??
                strings.genericErrorMessage,
            });
          }
        })
        .finally(() => setIsLoading(false));
    }
  };

  return (
    <UnauthLayout rightLink={{label: stringsCommon.login, to: "/login"}}>
      <form className={styles.registerInfo}>
        <div className={styles.registerText}>{stringsCommon.register}</div>
        <div className={styles.inputContainer}>
          <TextInput
            className={styles.registerInput}
            label={stringsCommon.firstName}
            type="text"
            autoComplete="given-name"
            value={firstName}
            onChangeText={(text) => {
              if (isValidFirstOrLastName(text)) {
                setFirstName(text);
                setFieldErrors((prev) => ({
                  ...prev,
                  [errorFieldNames.firstName]: [],
                }));
              }
            }}
            errorText={fieldErrors[errorFieldNames.firstName]?.join("\n")}
            maxLength={50}
          />
          <TextInput
            className={styles.registerInput}
            label={stringsCommon.lastName}
            type="text"
            autoComplete="family-name"
            value={lastName}
            onChangeText={(text) => {
              if (isValidFirstOrLastName(text)) {
                setLastName(text);
                setFieldErrors((prev) => ({
                  ...prev,
                  [errorFieldNames.lastName]: [],
                }));
              }
            }}
            errorText={fieldErrors[errorFieldNames.lastName]?.join("\n")}
            maxLength={50}
          />
          <StyledDateInput
            className={styles.registerInput}
            label={stringsCommon.dateOfBirth}
            autoComplete="bday"
            value={dob}
            onChangeDate={(date) => {
              setDOB(date);
              setFieldErrors((prev) => ({
                ...prev,
                [errorFieldNames.dateOfBirth]: [],
              }));
            }}
            errorText={fieldErrors[errorFieldNames.dateOfBirth]?.join("\n")}
          />
          <Select
            className={styles.patientNameSelect}
            value={gender}
            options={[
              {label: strings.gender, value: 0},
              {label: strings.male, value: 1},
              {label: strings.female, value: 2},
            ]}
            onChange={(v) => setGender(v)}
          />
          <TextInput
            label={strings.phone}
            type="tel"
            autoComplete="phone-number"
            value={phone}
            onChangeText={setPhone}
          />
          <TextInput
            className={styles.registerInput}
            label={stringsCommon.email}
            type="email"
            autoComplete="email"
            value={email}
            onChangeText={(text) => {
              if (isValidEmail(text)) {
                setEmail(text);
                setFieldErrors((prev) => ({
                  ...prev,
                  [errorFieldNames.email]: [],
                }));
              }
            }}
            errorText={fieldErrors[errorFieldNames.email]?.join("\n")}
            maxLength={50}
          />
          <TextInput
            label={strings.street}
            type="text"
            autoComplete="address"
            value={street}
            onChangeText={(val) => {
              if (isValidStreet(val)) {
                setStreet(val);
              }
            }}
            maxLength={200}
          />
          <TextInput
            label={strings.city}
            type="text"
            autoComplete="city"
            value={city}
            onChangeText={(val) => {
              if (isValidCity(val)) {
                setCity(val);
              }
            }}
            maxLength={100}
          />
          <div className={styles.bottomInput}>
            <StateInput
              value={state}
              onChange={setState}
              placeholder={strings.state}
              className={styles.bottomInputItem}
            />
            <TextInput
              label={strings.zip}
              type="text"
              autoComplete="zip"
              value={zip}
              onChangeText={(val) => {
                if (isValidZipCode(val)) {
                  setZip(val);
                }
              }}
              className={styles.bottomInputItem}
              maxLength={10}
            />
          </div>
          <TextInput
            className={styles.registerInput}
            label={stringsCommon.password}
            type="password"
            autoComplete="new-password"
            value={password}
            onChangeText={setPasswordVal}
            errorText={fieldErrors[errorFieldNames.password]?.join("\n")}
          />
          <TextInput
            className={styles.registerInput}
            label={strings.confirmPassword}
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChangeText={setConfirmPasswordVal}
            errorText={fieldErrors[errorFieldNames.confirmPassword]?.join("\n")}
          />
          <PasswordRequirements password={password} />
          <div className={styles.insuranceInstructions}>
            {strings.provideInsurance}
          </div>
          <div className={styles.insuranceSwitch}>
            <Switch checked={noInsurance} onChange={setNoInsurance} />
            <div>{strings.doNotHaveInsurance}</div>
          </div>
        </div>
        <Button
          className={styles.addInsuranceButton}
          variant="unfilled"
          iconLeft={(color: string) => <CameraIcon color={color} />}
          onClick={(e) => {
            e.preventDefault();
            setShowInsuranceModal(true);
          }}
          disabled={noInsurance}
        >
          {cardFront || cardBack ? strings.updateCard : strings.addCard}
        </Button>
        <Button
          onClick={registerNewUser}
          variant="filled"
          className={styles.submit}
          disabled={
            !firstName ||
            !lastName ||
            !dob ||
            !phone ||
            !email ||
            !street ||
            !city ||
            !state ||
            !zip ||
            !password ||
            !confirmPassword ||
            (!noInsurance && (!cardFront || !cardBack))
          }
          loading={isLoading}
        >
          {stringsCommon.submit}
        </Button>
      </form>
      <AddInsuranceCardModal
        visible={showInsuranceModal}
        onClose={() => setShowInsuranceModal(false)}
        onSave={(frontImage: File, backImage: File) => {
          setCardFront(frontImage);
          setCardBack(backImage);
          setShowInsuranceModal(false);
        }}
        frontCard={cardFront ?? undefined}
        backCard={cardBack ?? undefined}
      />
    </UnauthLayout>
  );
}
